(ns app.front.view.property.mass-delete
  (:require [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.view.not-found :as not-found]
            [app.front.view.property.dashboard.subs :as property-dashboard-subs]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.session :as session]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))



(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ domain-group-id]]
    (when (ff/enabled? :feature-flag.id/property-mass-delete-domain-group-properties)
      {:db (assoc-in db [::state ::route-domain-group-id] domain-group-id)
       :fx [[:dispatch [::fetch-creation-data]]]})))

(rf/reg-event-fx ::fetch-creation-data
  (fn [_ _]
    {:comm/command {:command    :property-management/creation-data
                    :on-success [::creation-data.success]
                    :method     :get}}))

(defn- domain-group-by-id [domain-groups domain-group-id]
  (some->> domain-groups
           (filter #(= (:db/id %) domain-group-id))
           first))

(rf/reg-event-fx ::creation-data.success
  (fn [{:keys [db]} [_ _ creation-data]]
    (let [domain-group-id       (get-in db [::state ::route-domain-group-id])
          selected-domain-group (or (domain-group-by-id creation-data domain-group-id)
                                    (first creation-data))]
      {:db       (-> db
                     (assoc-in [::property-dashboard-subs/initial-data :authorized-dgs] creation-data)
                     (assoc-in [::state ::domain-group] selected-domain-group))
       :dispatch [::fetch-jobs (:db/id selected-domain-group)]})))

(rf/reg-event-fx ::fetch-jobs
  (fn [_ [_ domain-group-id]]
    {:comm/command {:command    :property-management/mass-deletion-jobs
                    :data       (p/assoc-if {} :domain-group-id domain-group-id)
                    :method     :get
                    :on-success [::entities/merge-entity-db]}}))

(rf/reg-event-fx ::start-deletion
  (fn [_ [_ domain-group-id]]
    {:comm/command {:command    :property-management/mass-delete-domain-group-properties
                    :data       (util/mmap domain-group-id)
                    :on-success [::start-deletion.success domain-group-id]
                    :notify     {:success :property-mass-delete/started}}}))

(rf/reg-event-fx ::start-deletion.success
  (fn [_ [_ domain-group-id status changes]]
    {:fx [[:dispatch [::entities/apply-changes status changes]]
          [:dispatch [::fetch-jobs domain-group-id]]]}))

(rf/reg-event-fx ::set-domain-group
  (rf/path [::state])
  (fn [{:keys [db]} [_ domain-group]]
    {:db       (assoc db ::domain-group domain-group)
     :dispatch [::fetch-jobs (:db/id domain-group)]}))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::domain-group
  :<- [::state]
  :<- [::property-dashboard-subs/default-domain-group]
  (fn [[state default-domain-group] _]
    (or (::domain-group state)
        default-domain-group)))

(rf/reg-sub ::jobs
  :<- [::entities/context]
  :<- [::domain-group]
  (fn [[ctx domain-group] _]
    (->> (entities/find-entities ctx {:entity/type    :entity.type/async-job
                                      :async-job/task :async-job.task/property-mass-deletion})
         (filter #(= (:db/id domain-group)
                     (-> % :async-job/group :db/id)))
         (sort-by :entity/created >))))

(rf/reg-sub ::running-domain-group-ids
  :<- [::jobs]
  (fn [jobs _]
    (->> jobs
         (filter (du/in-state-fn :async-job.state/running))
         (map (comp :db/id :async-job/group))
         set)))

(rf/reg-sub ::selected-domain-group-running?
  :<- [::domain-group]
  :<- [::running-domain-group-ids]
  (fn [[domain-group running-domain-group-ids] _]
    (contains? running-domain-group-ids (:db/id domain-group))))

(rf/reg-sub ::back-href
  (fn [_]
    (href/href :view/property-dashboard)))

(rf/reg-sub ::view-data
  :<- [::back-href]
  (fn [back-href]
    {:back-href back-href}))

(defn- status [job]
  (case (:domain/state job)
    :async-job.state/running :warning
    :async-job.state/ready :ok
    :async-job.state/failed :critical
    nil))

(defn- group-selector [tr selected-domain-group]
  (when (<sub [::property-dashboard-subs/multiple-domain-groups?])
    [ui-select/select {:id           "property-mass-delete-domain-group-select"
                       :class        "full-width-select"
                       :allow-empty? false
                       :label-text   (tr [:property/select-domain-group])
                       :on-change    #(>evt [::set-domain-group %])
                       :required     true
                       :val->str     :group/name
                       :value        selected-domain-group
                       :values       (<sub [::property-dashboard-subs/domain-groups])}]))

(defn- start-controls [tr]
  (let [selected-domain-group (<sub [::domain-group])
        running?              (<sub [::selected-domain-group-running?])]
    [:section.p-8
     [ui/help (tr [:property-mass-delete/help])]
     [ui/warning (tr [:property-mass-delete/warning])]
     (if selected-domain-group
       [:div.flex.gap-8.items-end.mt-6
        (when (<sub [::property-dashboard-subs/multiple-domain-groups?])
          [:div.min-w-80
           [group-selector tr selected-domain-group]])
        [ui/modal-confirmation-button
         {:id                  "property-mass-delete-start"
          :tr                  tr
          :trigger-button-text (tr [:property-mass-delete/start])
          :trigger-button-opts {:kind     :danger
                                :size     :field
                                :disabled running?}
          :on-yes-click        #(>evt [::start-deletion (:db/id selected-domain-group)])}
         (tr [:property-mass-delete/confirm] [(:group/name selected-domain-group)])]
        (when running?
          [:div.flex.h-10.items-center
           (cc/inline-loading {:class-name  "min-h-0"
                               :description (tr [:property-mass-delete/running])})])]
       [:div.mt-6 (tr [:property/no-available-domain-groups])])]))

(defn- jobs-table [tr]
  (let [format-date (<sub [::date-util/date-time-fn])]
    [ui-table/modern-responsive-table
     {:tr                tr
      :items             (<sub [::jobs])
      :id-func           :db/id
      :get-status        status
      :empty-placeholder (tr [:property-mass-delete/no-jobs])
      :cols              [[:group/name
                           (comp :group/name :async-job/group)]
                          [:entity/created
                           (comp format-date :entity/created)]
                          [:async-job/updated
                           #(some->> (:async-job/updated %) format-date)]
                          [:domain/state
                           (comp tr vector :domain/state)]
                          [:property-mass-delete/progress
                           #(str (or (:async-job/processed-count %) 0)
                                 " / "
                                 (or (:async-job/total-count %) "-"))]
                          [:async-job/error
                           :async-job/error]]}]))

(defn view [match]
  (let [tr (<sub [:tr])]
    (if (<sub [::ff/enabled? :feature-flag.id/property-mass-delete-domain-group-properties])
      [:div.cloudpermit-page
       [ui/page-header (util/mmap match)
        [:h1 (tr [:property-mass-delete/title])]]
       [start-controls tr]
       [:section.p-8.mt-8
        [:div.flex.justify-between.items-center.mb-4
         [:h3 (tr [:property-mass-delete/jobs])]
         [ui/button {:kind     :secondary
                     :size     :field
                     :on-click #(>evt [::fetch-jobs (:db/id (<sub [::domain-group]))])}
          (tr [:refresh])]]
        [jobs-table tr]]]
      [not-found/not-found-view])))

(def route
  {:name        :view/property-mass-delete
   :parameters  {:query {(s/optional-key :domain-group-id) s/Int}}
   :view-sub    [::view-data]
   :view        view
   :controllers [{:parameters {:query [:domain-group-id]}
                  :start      (fn [{{:keys [domain-group-id]} :query}]
                                (>evt [::session/wait-for-user-data [::init domain-group-id]]))}]})
